import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';

import { LocationNodeData } from '../../../../../@types/live-search.type';
import { useSearchPartsOrPartTypes } from '../../../../../queries/live-search-query';
import { useCreateShipment, useCreateShipmentServiceAllocation } from '../../../../../queries/shipment-query';
import { LogTypeNames } from '../../../../../configs/enums';
import { fieldSx, ManifestEntry } from './PartsShipmentsHelpers';
import { NodeSearchField } from './NodeSearchField';
import { ManifestSection } from './ManifestSection';

export interface CreateShipmentFormProps {
  serviceId: number;
  sourceNodeId: number;
  sourceName: string;
  destinationName: string;
  clientHierarchy?: string;
  clientContractIds?: number[];
  clientName?: string;
  contractName?: string;
  onCreated: () => void;
  onCancel: () => void;
}

export const CreateShipmentForm: React.FC<CreateShipmentFormProps> = ({
  serviceId, sourceNodeId, sourceName, destinationName,
  clientHierarchy, clientContractIds, clientName, contractName,
  onCreated, onCancel,
}) => {
  const [source, setSource] = useState<LocationNodeData | null>(
    sourceNodeId ? { id: sourceNodeId, name: sourceName } as LocationNodeData : null,
  );
  const [destination, setDestination] = useState<LocationNodeData | null>(null);
  const [note, setNote] = useState('');
  const [ref1, setRef1] = useState('');
  const [ref2, setRef2] = useState('');
  const [ref3, setRef3] = useState('');
  const [bypassReceipt, setBypassReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [manifest, setManifest] = useState<ManifestEntry[]>([]);
  const [partSearchInput, setPartSearchInput] = useState('');
  const [partOptions, setPartOptions] = useState<Array<Record<string, unknown>>>([]);

  const partSearch = useSearchPartsOrPartTypes();
  const createShipment = useCreateShipment();
  const linkShipment = useCreateShipmentServiceAllocation();

  useEffect(() => {
    if (partSearchInput.length < 2) { setPartOptions([]); return; }
    const timer = setTimeout(() => {
      partSearch.mutateAsync({
        name: partSearchInput,
        clientContractIds: clientContractIds ?? [],
        locationId: source?.id ?? sourceNodeId,
        allowedPartLocationType: 'node',
        searchType: 'part-and-part-type',
      })
        .then((data) => setPartOptions(data as unknown as Array<Record<string, unknown>>))
        .catch(() => setPartOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [partSearchInput, source]);

  const addManifestItem = (item: Record<string, unknown>) => {
    const serial = String(item.serial1 ?? '');
    const partType = item.partType as Record<string, unknown> | undefined;
    const ptName = String(partType?.name ?? item.name ?? '-');
    const ptNo = String(partType?.number ?? item.partTypeNumber ?? '-');
    setManifest((prev) => [...prev, {
      key: `${item.id ?? Date.now()}-${serial || Math.random()}`,
      type: serial ? 'serialised' : 'non-serialised',
      partTypeName: ptName,
      partTypeNo: ptNo,
      serial: serial || undefined,
      quantity: 1,
    }]);
    setPartSearchInput('');
    setPartOptions([]);
  };

  const removeItem = (key: string) => setManifest((p) => p.filter((m) => m.key !== key));
  const updateQty = (key: string, q: number) =>
    setManifest((p) => p.map((m) => m.key === key ? { ...m, quantity: Math.max(1, q) } : m));

  const exportManifest = () => {
    const rows = [
      ['Type', 'Part Type', 'Part No', 'Serial', 'Qty'].join(','),
      ...manifest.map((m) => [m.type, m.partTypeName, m.partTypeNo, m.serial ?? '', m.quantity].join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `manifest-JOB-${serviceId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleSubmit = async () => {
    if (!source) { setError('Source is required'); return; }
    if (!destination) { setError('Destination is required'); return; }
    setError(''); setSaving(true);
    try {
      const shipment = await createShipment.mutateAsync({
        source: source.name, sourceNodeId: source.id,
        destination: destination.name, destinationNodeId: destination.id,
        shipmentStatusCode: 'in-transit', shipmentTypeCode: 'dispatch',
        clientHierarchy: clientHierarchy ?? '',
        clientHierarchyName: `${clientName ?? ''} → ${contractName ?? ''}`,
        client: clientName ?? '', contract: contractName ? [contractName] : [],
        serviceId, connoteNo: '',
        note: note || undefined,
        referenceNumber1: ref1 || undefined,
        referenceNumber2: ref2 || undefined,
        referenceNumber3: ref3 || undefined,
        bypassReceipt,
        manifest: manifest.map((m) => ({
          partTypeName: m.partTypeName, partTypeNo: m.partTypeNo,
          serial: m.serial, quantity: m.quantity,
        })),
      });
      await linkShipment.mutateAsync({
        shipmentId: shipment.id, serviceId,
        logType: LogTypeNames.NewShipmentCreated,
      });
      onCreated();
    } catch { setError('Failed to create shipment'); }
    finally { setSaving(false); }
  };

  const serialised = manifest.filter((m) => m.type === 'serialised');
  const nonSerialised = manifest.filter((m) => m.type === 'non-serialised');
  const refLabelSx = { fontSize: '9px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' as const, letterSpacing: '0.04em', mb: 0.25 };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'v4.border', borderRadius: 2, p: 2, mt: 1, bgcolor: 'v4.background' }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1.5 }}>Create New Shipment</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <NodeSearchField label="Source *" defaultName={sourceName} onChange={setSource} />
        <NodeSearchField label="Destination *" defaultName={destinationName} onChange={setDestination} />
        <Box>
          <Typography sx={refLabelSx}>Note</Typography>
          <InputBase placeholder="Optional note..." value={note} onChange={(e) => setNote(e.target.value)}
            multiline minRows={2} sx={fieldSx} inputProps={{ 'aria-label': 'Note' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box><Typography sx={refLabelSx}>Reference 1</Typography><InputBase value={ref1} onChange={(e) => setRef1(e.target.value)} sx={fieldSx} /></Box>
          <Box><Typography sx={refLabelSx}>Reference 2</Typography><InputBase value={ref2} onChange={(e) => setRef2(e.target.value)} sx={fieldSx} /></Box>
          <Box><Typography sx={refLabelSx}>Reference 3</Typography><InputBase value={ref3} onChange={(e) => setRef3(e.target.value)} sx={fieldSx} /></Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 1, fontSize: '11px', color: 'text.secondary' }}>
        <span>Client/Contract: <strong>{clientName ?? '—'} → {contractName ?? '—'}</strong></span>
        <span>Service: <strong>JOB-{serviceId}</strong></span>
      </Box>

      <FormControlLabel
        control={<Checkbox size="small" checked={bypassReceipt} onChange={(e) => setBypassReceipt(e.target.checked)} />}
        label="Bypass Receipt" slotProps={{ typography: { sx: { fontSize: '12px' } } }} sx={{ mt: 0.5 }} />

      <ManifestSection
        manifest={manifest} serialised={serialised} nonSerialised={nonSerialised}
        partSearchInput={partSearchInput} partOptions={partOptions}
        partSearchPending={partSearch.isPending}
        onPartSearchInputChange={setPartSearchInput}
        onAddItem={addManifestItem} onRemoveItem={removeItem}
        onUpdateQty={updateQty} onExport={exportManifest}
      />

      {error && <Typography sx={{ fontSize: '11px', color: 'error.main', mt: 1 }}>{error}</Typography>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
        <Box component="button" onClick={onCancel}
          sx={{ fontSize: '11px', px: 1.5, py: 0.5, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', fontFamily: 'inherit' }}>
          Cancel
        </Box>
        <Box component="button" onClick={handleSubmit} disabled={saving}
          sx={{ fontSize: '11px', fontWeight: 600, px: 2, py: 0.5, border: 'none', borderRadius: '4px', bgcolor: 'v4.accent', color: 'common.white', cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Creating...' : 'Create & Link'}
        </Box>
      </Box>
    </Box>
  );
};
